/**
 * Last Full Media / Capture selection for a media identity.
 *
 * Survives regular ↔ fullscreen MediaDisplay remounts (the owner returns null
 * while the app-level viewer mounts, and vice versa on minimize). New identities
 * have no memory so ``checkSourceInfo`` can still pick a default.
 */

export type CaptureViewHandoff = {
  viewCapture: boolean;
  previewSrc?: string;
  /**
   * ``mediaContext.capture`` as it stood when the choice was made, ``''`` when
   * the media had none. Absent on entries recorded without one.
   */
  captureRef?: string;
};

const captureViewByKey = new Map<string, CaptureViewHandoff>();

export function rememberCaptureView(
  key: string,
  viewCapture: boolean,
  previewSrc?: string,
  captureRef?: string,
): void {
  if (!key) return;
  const trimmed = previewSrc?.trim();
  const ref = captureRef?.trim();
  captureViewByKey.set(key, {
    viewCapture,
    ...(trimmed ? { previewSrc: trimmed } : {}),
    ...(ref !== undefined ? { captureRef: ref } : {}),
  });
}

/**
 * Mirror a mounted viewer's current selection without re-attributing it.
 *
 * Applying a capture does not change the media's identity key, so a viewer that
 * never re-derived ``viewCapture`` keeps a Full Media choice made back when the
 * media had no crop. Recording that with the live ``mediaContext.capture`` would
 * present it as a choice made *about* the new crop, and
 * ``resolveInitialViewCapture`` would then honor it — reopening a remounted
 * viewer on Full Media over the crop the tile is painting, which is the leak
 * ``rememberedChoiceIsStale`` exists to close (TR-3019).
 *
 * Re-recording the same side therefore keeps the attribution as it stands.
 * Switching sides is a decision, so it stamps the capture it was made about;
 * an explicit Use Full Media / Use Capture that re-confirms the side already
 * showing goes through ``rememberCaptureView`` instead.
 */
export function mirrorCaptureViewState(
  key: string,
  viewCapture: boolean,
  previewSrc?: string,
  captureRef?: string,
): void {
  if (!key) return;
  const recorded = captureViewByKey.get(key);
  // Nothing recorded yet: the caller's selection was itself derived from the
  // live capture at mount, so that capture is what this choice is about.
  const sameSideAsRecorded = recorded !== undefined && recorded.viewCapture === viewCapture;
  rememberCaptureView(
    key,
    viewCapture,
    previewSrc,
    sameSideAsRecorded ? recorded.captureRef : captureRef,
  );
}

export function getRememberedCaptureView(key: string): CaptureViewHandoff | undefined {
  if (!key) return undefined;
  return captureViewByKey.get(key);
}

/**
 * A remembered selection speaks only for the capture it was made about.
 *
 * ``Full Media`` picked while the media had no capture — or had an older one —
 * must not suppress a capture applied afterwards. Without this, the Full Media
 * entry left behind by the first pass through the event-editor thumbnail flow
 * (and by any still-mounted viewer that never re-derived its own selection,
 * since applying a capture does not change the media's identity key) reopened
 * the viewer on Full Media even though the tile painted the new crop (TR-3019).
 */
function rememberedChoiceIsStale(
  remembered: CaptureViewHandoff,
  currentCaptureRef: string | undefined,
): boolean {
  // Either side unknown: no basis to reject, so keep the recorded choice.
  if (currentCaptureRef === undefined || remembered.captureRef === undefined) {
    return false;
  }
  return remembered.captureRef !== currentCaptureRef.trim();
}

/**
 * First-paint Full Media / Capture selection.
 *
 * A remembered maximize/minimize (or in-session toggle) wins so regular ↔
 * fullscreen keeps the same Full Media | Capture side, unless the capture it
 * was made about has since changed. With no usable memory, the inline
 * presentation MediaPane prefers Capture when one exists; other surfaces use
 * the same capture-first default.
 */
export function resolveInitialViewCapture(args: {
  remembered?: CaptureViewHandoff;
  hasPersistedCapture: boolean;
  preferCapture?: boolean;
  currentCaptureRef?: string;
}): boolean {
  if (
    args.remembered !== undefined &&
    !rememberedChoiceIsStale(args.remembered, args.currentCaptureRef)
  ) {
    return args.remembered.viewCapture;
  }
  if (args.preferCapture && args.hasPersistedCapture) return true;
  return args.hasPersistedCapture;
}

/**
 * The selection the regular viewer should adopt as the maximize view closes,
 * or ``null`` to leave its current state alone.
 *
 * The owner MediaDisplay renders ``null`` while the app-level viewer is up
 * instead of unmounting, so it cannot rely on mount-time reads to pick up a
 * choice made in maximize.
 */
export function resolveCaptureViewOnFullscreenExit(args: {
  isAppFullscreenViewer: boolean;
  isFullscreen: boolean;
  wasFullscreen: boolean;
  showsMaximizedMedia: boolean;
  remembered: CaptureViewHandoff | undefined;
}): CaptureViewHandoff | null {
  // The maximize view is the writer, not a reader, and only the true → false
  // edge is a return trip.
  if (args.isAppFullscreenViewer || args.isFullscreen || !args.wasFullscreen) {
    return null;
  }
  // ``isFullscreen`` is context-wide, so every viewer on the page sees this
  // edge. Only the ones showing the media that was actually maximized have a
  // selection to take back; the rest would re-apply their own stale memory.
  if (!args.showsMaximizedMedia) return null;
  return args.remembered ?? null;
}

export type DisplayedMediaChoiceCommit = 'full' | 'capture';

/**
 * Whether closing the maximized view should write the Full Media | Capture
 * switcher onto the media row.
 *
 * Hosts that do not opt in (event editor, Displayed Media) keep the view-only
 * handoff. A Full Media selection is saved only when a capture is still on the
 * row; a Capture selection is saved only when a staged still differs from it.
 * Framing a new region forces the full-media view without being a choice, so
 * closing then must not clear the saved capture.
 */
export function displayedMediaChoiceToCommit(args: {
  commitViewChoiceOnClose: boolean;
  viewCapture: boolean;
  savedCapture: string;
  stagedCapture?: string;
  framingCapture?: boolean;
}): DisplayedMediaChoiceCommit | null {
  if (!args.commitViewChoiceOnClose || args.framingCapture) return null;
  const saved = args.savedCapture.trim();
  const staged = (args.stagedCapture ?? '').trim();
  if (args.viewCapture) {
    if (staged && staged !== saved) return 'capture';
    return null;
  }
  if (saved) return 'full';
  return null;
}

export function clearRememberedCaptureView(key?: string): void {
  if (key) {
    captureViewByKey.delete(key);
  } else {
    captureViewByKey.clear();
  }
}
